import { useHistory } from 'react-router';
import { Button } from '../../../shared/formElements/button';
import * as Yup from 'yup';
import {
  Form,
  FormContainer,
  FormSectionContainer,
  ButtonGroupContainer,
  SectionContainer,
  LoadingSpinner,
  Line,
} from '../../../shared/Layout.styles';
import { Markdown } from '../../../shared/markdown';
import {
  activateCardConstants,
  phoneMaxLength,
  phoneMinLength,
} from '../../../utils/constants/activate';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useContextDispatch, useContextState } from '../../context/context';
import { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { globalConstants } from '../../../utils/constants/global';
import { RequestCardInputs } from './sections/RequestCardInputs';
import ReCAPTCHA from 'react-google-recaptcha';
const {
  activationCode,
  email,
  firstName,
  lastName,
  phoneNumber,
  termsConditionsCheck,
} = activateCardConstants.validations;

declare global {
  interface Window {
    grecaptcha: any;
  }
}

export const EmailActivation = () => {
  const dispatch = useContextDispatch();
  const { loading } = useContextState();

  const [captchaToken, setCaptchaToken] = useState<string>("");
  const [isCaptchaCompleted, setCaptchaCompleted] = useState(false);
  const recaptcha = useRef<ReCAPTCHA>(null);

  const resetCaptcha = useCallback(() => {
    recaptcha.current?.reset();
    setCaptchaCompleted(false);
    setCaptchaToken("");
  }, []);

  const handleCaptchaChange = useCallback((value: string | null) => {
    setCaptchaCompleted(Boolean(value));
    setCaptchaToken(value || "");
  }, []);

  const [serverErrors, setServerErrors] = useState('');

  const { expiredCardMessage } = globalConstants;

  const { push } = useHistory();

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema),
    shouldFocusError: false,
    defaultValues: {
      activationCode: '',
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      termsConditionsCheck: false,
    },
  });

  const onSubmit = useCallback(async (formData: any) => {
    if (!isCaptchaCompleted) {
      setServerErrors('Captcha not verified');
      return;
    }
    try {
      dispatch('START_LOADING');
      const data = {
        fname: formData.firstName,
        lname: formData.lastName,
        activationCode: formData.activationCode,
        email: formData.email,
        phone: formData.phoneNumber,
        captchaToken: captchaToken,
      };
      await axios.post('/v1/validateemailcard', data, {
        headers: {
          'X-API-Key': process.env.REACT_APP_VAULT_API_KEY || ''
        },
      });
      dispatch('STOP_LOADING');
      let successLocation = {
        pathname: '/success',
        state: { message: `#### Thank you. You will receive your digital card, sent via SMS to the mobile number provided within the next 2 hours` }
      }

      push(successLocation);
      resetCaptcha();
    } catch (err: any) {
      console.log('err', err);
      if (err.response.status === 400) {
        setServerErrors(err.response.data.message);
      } else if (err.response.status === 700) {
        setServerErrors(expiredCardMessage);
      } else {
        setServerErrors('Something went wrong');
      }
      dispatch('STOP_LOADING');
    }
  }, [captchaToken, dispatch, expiredCardMessage, push, resetCaptcha]);

  return (
    <>
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
          <SectionContainer align="center">
            <Markdown children="# Activate Prepaid Student Card - April 2026"  align="center"/>
            <Markdown children="Enter your details below:" align="center" />
            <FormContainer className="activateCardSection">
              <FormSectionContainer className="activateCardSection" >
                <RequestCardInputs />
                {serverErrors && (
                  <Markdown
                    children={serverErrors}
                    className="validationText"
                  />
                )}
                <ReCAPTCHA
                  ref={recaptcha}
                  sitekey="6LfU8UspAAAAAGYvuYdOkXrHcGGpuEXM4-hxH7Rh"
                  onChange={handleCaptchaChange}
                />
                <ButtonGroupContainer  align="center">
                  <Button
                    onClick={() => {
                      methods.handleSubmit(onSubmit);
                    }}
                  >
                    {loading ? <LoadingSpinner show /> : 'Request Card'}
                  </Button>
                </ButtonGroupContainer>
              </FormSectionContainer>
            </FormContainer>
          </SectionContainer>
        </Form>
      </FormProvider>
    </>
  );
};
const validationSchema = Yup.object().shape({
  activationCode: Yup.string()
    .required(activationCode.required)
    .matches(/^[a-zA-Z0-9_.-]*$/, {
      message: activationCode.format,
      excludeEmptyString: true,
    }),
  firstName: Yup.string()
    .required(firstName.required)
    .matches(/^([A-Za-z\s\-'".])*$/, {
      message: firstName.format,
      excludeEmptyString: true,
    }).min(1, firstName.minLength)
    .max(20, firstName.maxLength),
  lastName: Yup.string()
    .required(lastName.required)
    .matches(/^([A-Za-z\s\-'".])*$/, {
      message: lastName.format,
      excludeEmptyString: true,
    }).min(1, lastName.minLength)
    .max(30, lastName.maxLength),
  email: Yup.string().email(email.format).required(email.required),
  phoneNumber: Yup.string()
    .required(phoneNumber.required)
    .matches(/^(?:\+614\d{8}|04\d{8})$/, {
      message: phoneNumber.format,
      excludeEmptyString: true,
    })
    .min(phoneMinLength, phoneNumber.minLength)
    .max(phoneMaxLength, phoneNumber.maxLength),
  confirmPhoneNumber: Yup.string().oneOf(
    [Yup.ref('phoneNumber'), null],
    "Mobiles numbers don't match"
  ),
  termsConditionsCheck: Yup.bool().oneOf([true], termsConditionsCheck.required),
});
